import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, CheckCircle2, Gift, HeartHandshake, Home, Palette, Sparkles, Users } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { absoluteUrl, SITE_NAME } from '@/constants/site';
import { trackFunnelEvent } from '@/services/FunnelAnalytics';

const recipientIdeas = [
  {
    icon: Users,
    title: 'Pour les parents et grands-parents',
    photo: 'Photo de famille récente, portrait avec les enfants, souvenir de vacances ou moment multi-générationnel.',
    style: 'Aquarelle pour la douceur, peinture à l’huile pour un tableau plus premium à accrocher au salon.',
    source: 'family_recipient',
  },
  {
    icon: HeartHandshake,
    title: 'Pour un couple ou un conjoint',
    photo: 'Photo de couple, mariage, voyage, maison, animal commun ou lieu qui raconte une histoire partagée.',
    style: 'Impressionniste ou huile pour une ambiance chaleureuse, croquis pour un cadeau sobre et élégant.',
    source: 'couple_recipient',
  },
  {
    icon: Home,
    title: 'Pour une décoration de maison',
    photo: 'Paysage familial, maison de famille, animal de compagnie ou souvenir qui reste lisible de loin.',
    style: 'Aquarelle, peinture à l’huile ou impressionniste selon la pièce et les couleurs déjà présentes.',
    source: 'home_decor',
  },
];

const christmasChecklist = [
  'Privilégiez une photo claire où le sujet principal se reconnaît immédiatement.',
  'Choisissez un souvenir personnel plutôt qu’une image trop parfaite mais froide.',
  'Évitez les photos sombres ou très chargées si le tableau doit être exposé au mur.',
  'Gardez le message simple : l’émotion doit venir du souvenir transformé en œuvre.',
];

const styleSuggestions = [
  {
    to: '/styles/watercolor',
    title: 'Aquarelle',
    helper: 'Un rendu doux, lumineux et facile à offrir à Noël à un parent ou grand-parent.',
  },
  {
    to: '/styles/oil-painting',
    title: 'Peinture à l’huile',
    helper: 'Pour un cadeau de Noël plus premium, décoratif et durable.',
  },
  {
    to: '/styles/impressionist',
    title: 'Impressionniste',
    helper: 'Idéal pour une ambiance chaleureuse, un paysage ou un souvenir familial.',
  },
  {
    to: '/styles/sketch',
    title: 'Croquis',
    helper: 'Une option sobre si la photo porte déjà beaucoup d’émotion.',
  },
];

const planningSteps = [
  {
    title: '1. Partir de la personne à gâter',
    text: 'Un cadeau de Noël personnalisé fonctionne mieux quand il répond à un lien précis : famille, couple, animal, maison ou souvenir partagé.',
  },
  {
    title: '2. Sélectionner une photo sûre',
    text: 'Le meilleur choix n’est pas toujours la photo la plus professionnelle, mais celle qui sera comprise et reconnue dès l’ouverture du cadeau.',
  },
  {
    title: '3. Choisir un style qui s’intègre chez elle',
    text: 'Doux, premium, graphique ou minimaliste : le style doit aider le tableau à trouver naturellement sa place dans la décoration.',
  },
];

const relatedPages = [
  {
    to: '/idees-cadeaux-photo-personnalises',
    title: 'Idées cadeaux photo personnalisés',
    helper: 'Comparer les occasions et trouver d’autres idées cadeaux à partir d’une photo.',
  },
  {
    to: '/cadeau-anniversaire-photo-personnalise',
    title: 'Cadeau anniversaire photo',
    helper: 'Adapter la même logique cadeau à un anniversaire ou une fête familiale.',
  },
  {
    to: '/portrait-animal-personnalise',
    title: 'Portrait animal personnalisé',
    helper: 'Une idée forte pour les amoureux des chiens, chats et compagnons de famille.',
  },
];

const createPath = (source: string) => `/create?source=christmas_gift_${source}`;

const trackChristmasCta = (source: string) => {
  trackFunnelEvent('seo_landing_cta_clicked', {
    page: 'cadeau-noel-photo-personnalise',
    source: `christmas_gift_${source}`,
  });
};

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': absoluteUrl('/cadeau-noel-photo-personnalise'),
      name: 'Cadeau Noël photo personnalisé',
      description:
        'Guide pour créer un cadeau de Noël photo personnalisé : choix de la photo, style artistique, destinataire et création d’un tableau personnalisé.',
      url: absoluteUrl('/cadeau-noel-photo-personnalise'),
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: absoluteUrl(),
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: absoluteUrl(),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cadeau Noël photo personnalisé',
          item: absoluteUrl('/cadeau-noel-photo-personnalise'),
        },
      ],
    },
  ],
};

const CadeauNoelPhotoPersonnalise = () => {
  return (
    <>
      <SEOHead
        title="Cadeau Noël photo personnalisé — Tableau unique | Ma Photo en Tableau"
        description="Créez un cadeau de Noël photo personnalisé à partir d’un souvenir : conseils photo, styles artistiques, idées par destinataire et création de tableau personnalisé."
        canonical={absoluteUrl('/cadeau-noel-photo-personnalise')}
        schema={schema}
      />
      <div className="min-h-screen overflow-x-hidden">
        <main>
          <section className="section-shell gradient-hero relative overflow-hidden pt-32 pb-20">
            <div className="hero-orb left-[8%] top-28 h-32 w-32 bg-accent/40" />
            <div className="hero-orb right-[12%] top-24 h-40 w-40 bg-primary/25 [animation-delay:2s]" />
            <div className="container relative mx-auto px-6">
              <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-primary/85 shadow-card backdrop-blur-sm">
                    Cadeau Noël photo personnalisé
                  </p>
                  <h1 className="display-title mb-6 text-foreground">
                    Transformez une photo de famille en <span className="text-premium">cadeau de Noël vraiment personnel</span>.
                  </h1>
                  <p className="mb-8 max-w-2xl text-lg leading-8 text-foreground/72 md:text-xl">
                    Créez un tableau personnalisé à partir d’un souvenir : famille, couple, animal ou moment de maison. Le parcours aide à choisir une photo lisible, un style artistique et un rendu prêt à offrir.
                  </p>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Button variant="premium" size="lg" className="h-14 rounded-full px-8 text-base shadow-glow" asChild>
                      <Link to={createPath('hero_primary')} onClick={() => trackChristmasCta('hero_primary')}>
                        Créer mon cadeau de Noël
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" className="h-14 rounded-full border-white/70 bg-white/60 px-8 text-base" asChild>
                      <Link to="/idees-cadeaux-photo-personnalises">Voir toutes les idées cadeaux</Link>
                    </Button>
                  </div>
                </div>

                <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-6 shadow-luxury backdrop-blur-xl">
                  <Gift className="mb-5 h-10 w-10 text-primary" />
                  <h2 className="font-display text-4xl leading-none text-foreground">La bonne idée : offrir un souvenir qui reste visible après les fêtes.</h2>
                  <div className="mt-6 space-y-4">
                    {christmasChecklist.map((tip) => (
                      <div key={tip} className="flex gap-3 text-sm leading-7 text-foreground/68">
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 max-w-3xl">
                <p className="eyebrow mb-3">Choix rapide</p>
                <h2 className="section-title text-foreground">Quel cadeau photo de Noël selon le destinataire ?</h2>
                <p className="mt-4 text-lg leading-8 text-foreground/65">
                  Une page cadeau convertit mieux quand le visiteur retrouve vite son cas : parents, couple, grands-parents, animal de compagnie ou décoration de maison.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                {recipientIdeas.map((idea) => (
                  <article key={idea.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                      <idea.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-3xl leading-none text-foreground">{idea.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Photo conseillée :</span> {idea.photo}</p>
                    <p className="mt-3 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Style :</span> {idea.style}</p>
                    <Button variant="premium" className="mt-6 rounded-full" asChild>
                      <Link to={createPath(idea.source)} onClick={() => trackChristmasCta(idea.source)}>
                        Créer pour ce profil
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-8 shadow-card backdrop-blur-md">
                  <Sparkles className="mb-5 h-8 w-8 text-primary" />
                  <h2 className="section-title text-foreground">Un cadeau personnel, pas un objet impersonnel de dernière minute.</h2>
                  <p className="mt-5 text-sm leading-7 text-foreground/68">
                    Un tableau photo personnalisé garde l’émotion du souvenir tout en devenant un objet décoratif. C’est une alternative utile aux cadeaux standardisés quand vous voulez offrir quelque chose de personnel sans multiplier les objets gadgets.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  {planningSteps.map((step) => (
                    <div key={step.title} className="rounded-[1.5rem] border border-white/65 bg-white/60 p-5 shadow-card">
                      <h3 className="font-display text-2xl leading-none text-foreground">{step.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-foreground/62">{step.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-3">Styles conseillés</p>
                  <h2 className="section-title text-foreground">Quel style artistique pour un cadeau de Noël ?</h2>
                </div>
                <Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild>
                  <Link to={createPath('style_section')} onClick={() => trackChristmasCta('style_section')}>Tester avec ma photo</Link>
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {styleSuggestions.map((style) => (
                  <Link key={style.to} to={style.to} className="group rounded-[1.6rem] border border-white/65 bg-white/60 p-5 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <Palette className="mb-4 h-6 w-6 text-primary" />
                    <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{style.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-foreground/62">{style.helper}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Voir le style
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="rounded-[2.4rem] border border-white/70 bg-white/70 p-8 shadow-luxury backdrop-blur-xl md:p-10">
                <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-center">
                  <div>
                    <CalendarDays className="mb-5 h-9 w-9 text-primary" />
                    <h2 className="section-title text-foreground">Préparez le cadeau avant la période chargée.</h2>
                    <p className="mt-5 text-base leading-8 text-foreground/66">
                      La meilleure expérience commence par une photo bien choisie et un style cohérent avec la personne. Vous pouvez lancer la création dès que l’idée est claire, puis avancer vers le support et la commande dans le parcours dédié.
                    </p>
                  </div>
                  <div className="rounded-[1.8rem] bg-primary/8 p-6">
                    <h3 className="font-display text-3xl leading-none text-foreground">Mini-checklist avant de créer</h3>
                    <ul className="mt-5 space-y-3 text-sm leading-7 text-foreground/66">
                      <li>• Une photo principale claire.</li>
                      <li>• Un destinataire ou une pièce en tête.</li>
                      <li>• Un style artistique adapté à l’ambiance voulue.</li>
                      <li>• Un souvenir assez personnel pour faire sourire dès l’ouverture.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="grid gap-5 md:grid-cols-3">
                {relatedPages.map((page) => (
                  <Link key={page.to} to={page.to} className="group rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{page.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-foreground/65">{page.helper}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Continuer le guide
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="rounded-[2.4rem] bg-foreground p-8 text-background shadow-luxury md:p-12">
                <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
                  <div>
                    <p className="eyebrow mb-3 text-background/55">Création guidée</p>
                    <h2 className="font-display text-5xl leading-none md:text-6xl">Créer un cadeau de Noël à partir d’une photo.</h2>
                    <p className="mt-5 max-w-2xl text-base leading-8 text-background/68">
                      Importez votre photo, choisissez un style artistique et avancez vers un tableau personnalisé prêt à devenir un cadeau vraiment personnel.
                    </p>
                  </div>
                  <Button variant="secondary" size="lg" className="h-14 rounded-full px-8 text-base" asChild>
                    <Link to={createPath('final_cta')} onClick={() => trackChristmasCta('final_cta')}>
                      Commencer la création
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CadeauNoelPhotoPersonnalise;
